/**
 * PEPulse — Social Media Tracker (Mobile-First + Leaderboard).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Film, Image, Layers, Loader2, Minus, Plus, Trash2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { auth } from '../../config/firebase';
import useAppStore from '../../store/index';
import useSocialMediaStore, { SocialMediaLog } from '../../store/socialMediaStore';
import { PE_PULSE_LOGO_SRC, PE_PULSE_ICON_SRC, PE_PULSE_MANIFEST_HREF } from '../../constants/brandAssets';
import {
    PACING_AMPEL_COLOR,
    SOCIAL_MEDIA_MONTHLY_TARGET_POINTS,
    SOCIAL_MEDIA_POINTS,
    SOCIAL_MEDIA_VACATION_BASE_DAYS,
    SocialMediaLogType,
    calendarPacingSoll,
    clampVacationDays,
    computeMonthlyStats,
    currentMonthKey,
    isMalusExemptEmail,
    scopePacingAmpel,
    todayIsoLocal,
} from '../../utils/socialMediaScoring';
import { mergeTeamPool, TeamMemberLike } from '../../utils/teamResolve';
import PepulseAdminCockpit from './PepulseAdminCockpit';

const STORY_PER_DAY = 3;
const STORIES_PER_WEEK = 15;
const POSTS_PER_WEEK = 2;
const REELS_PER_WEEK = 1;

/** Punkte-Ziele für Cockpit-Ansichten */
const DAY_TARGET_POINTS = STORY_PER_DAY * SOCIAL_MEDIA_POINTS.story; // 30
const WEEK_TARGET_POINTS =
    STORIES_PER_WEEK * SOCIAL_MEDIA_POINTS.story +
    POSTS_PER_WEEK * SOCIAL_MEDIA_POINTS.post +
    REELS_PER_WEEK * SOCIAL_MEDIA_POINTS.reel; // 250

type ProgressScope = 'day' | 'week' | 'month';

const RANK_DOTS = ['#22c55e', '#3b82f6', '#f97316', '#a855f7', '#eab308', '#ec4899', '#14b8a6'];

const TYPE_META: Record<
    SocialMediaLogType,
    { label: string; short: string; points: number; Icon: React.FC<{ size?: number; className?: string }> }
> = {
    story: { label: '+1 Story', short: 'Story', points: SOCIAL_MEDIA_POINTS.story, Icon: Image },
    post: { label: '+1 Beitrag', short: 'Beitrag', points: SOCIAL_MEDIA_POINTS.post, Icon: Layers },
    reel: { label: '+1 Reel', short: 'Reel', points: SOCIAL_MEDIA_POINTS.reel, Icon: Film },
};

const TYPE_ORDER: SocialMediaLogType[] = ['story', 'post', 'reel'];

const isSupportMember = (m: TeamMemberLike | null | undefined): boolean => {
    if (!m) return false;
    const category = String(m.category || '').toLowerCase();
    const role = String(m.role || '').toLowerCase();
    return category === 'support' || role === 'support';
};

/** Admin oder Coach (kein Support) */
const isPepulseEligibleMember = (m: TeamMemberLike | null | undefined): boolean => {
    if (!m || isSupportMember(m)) return false;
    const category = String(m.category || '').toLowerCase();
    const role = String(m.role || '').toLowerCase();
    return (
        ['coach', 'admin', 'superadmin'].includes(category) ||
        ['coach', 'admin', 'superadmin'].includes(role)
    );
};

/** ISO-KW: Montag → Sonntag (lokal) */
const getIsoWeekRange = (from: Date = new Date()): { startIso: string; endIso: string } => {
    const d = new Date(from.getFullYear(), from.getMonth(), from.getDate());
    const day = d.getDay();
    const diffToMon = day === 0 ? -6 : 1 - day;
    const mon = new Date(d);
    mon.setDate(d.getDate() + diffToMon);
    const sun = new Date(mon);
    sun.setDate(mon.getDate() + 6);
    const toIso = (x: Date) => {
        const y = x.getFullYear();
        const m = String(x.getMonth() + 1).padStart(2, '0');
        const dayNum = String(x.getDate()).padStart(2, '0');
        return `${y}-${m}-${dayNum}`;
    };
    return { startIso: toIso(mon), endIso: toIso(sun) };
};

const formatLogTime = (createdAt: unknown): string => {
    if (!createdAt) return '';
    try {
        let d: Date | null = null;
        if (typeof createdAt === 'string') d = new Date(createdAt);
        else if (typeof (createdAt as { toDate?: () => Date }).toDate === 'function') {
            d = (createdAt as { toDate: () => Date }).toDate();
        } else if (createdAt instanceof Date) d = createdAt;
        if (!d || Number.isNaN(d.getTime())) return '';
        return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    } catch {
        return '';
    }
};

const formatPoints = (n: number): string =>
    new Intl.NumberFormat('de-DE').format(Math.max(0, Math.round(n)));

/** Visuelles Konfetti — kein Audio */
const ConfettiBurst: React.FC<{ active: boolean }> = ({ active }) => {
    if (!active) return null;
    const pieces = Array.from({ length: 40 }, (_, i) => i);
    return (
        <div className="pointer-events-none absolute inset-0 overflow-hidden z-20 rounded-xl">
            {pieces.map((i) => (
                <motion.span
                    key={i}
                    className="absolute w-1.5 h-2.5 rounded-[1px]"
                    style={{
                        left: `${(i * 13) % 100}%`,
                        top: '5%',
                        backgroundColor: RANK_DOTS[i % RANK_DOTS.length],
                    }}
                    initial={{ y: 0, opacity: 1, rotate: 0, scale: 1 }}
                    animate={{
                        y: 220 + (i % 6) * 35,
                        opacity: [1, 1, 0],
                        rotate: 180 + i * 25,
                        scale: [1, 1.1, 0.6],
                    }}
                    transition={{ duration: 1.6 + (i % 5) * 0.15, ease: 'easeOut' }}
                />
            ))}
        </div>
    );
};

type MobileTrackerViewProps = {
    isAdmin?: boolean;
    payoutBaseByEmail?: Record<string, number>;
};

const MobileTrackerView: React.FC<MobileTrackerViewProps> = ({
    isAdmin = false,
    payoutBaseByEmail = {},
}) => {
    const { team, teamRegistry } = useAppStore((s: any) => ({
        team: s.team || [],
        teamRegistry: s.teamRegistry || [],
    }));

    const logs = useSocialMediaStore((s) => s.logs);
    const monthAllLogs = useSocialMediaStore((s) => s.monthAllLogs);
    const monthMetaByMonth = useSocialMediaStore((s) => s.monthMetaByMonth);
    const error = useSocialMediaStore((s) => s.error);
    const addLog = useSocialMediaStore((s) => s.addLog);
    const deleteLog = useSocialMediaStore((s) => s.deleteLog);
    const subscribeMonthlyLogs = useSocialMediaStore((s) => s.subscribeMonthlyLogs);
    const unsubscribeMonthlyLogs = useSocialMediaStore((s) => s.unsubscribeMonthlyLogs);
    const subscribeMonthAllLogs = useSocialMediaStore((s) => s.subscribeMonthAllLogs);
    const unsubscribeMonthAllLogs = useSocialMediaStore((s) => s.unsubscribeMonthAllLogs);
    const fetchMonthMeta = useSocialMediaStore((s) => s.fetchMonthMeta);
    const setVacationDays = useSocialMediaStore((s) => s.setVacationDays);

    const uid = auth.currentUser?.uid || '';
    const email = (auth.currentUser?.email || '').trim().toLowerCase();
    const isExempt = isMalusExemptEmail(email);
    const month = currentMonthKey();

    const fullTeam = useMemo(
        () => mergeTeamPool(team, teamRegistry),
        [team, teamRegistry]
    );

    const myProfile = useMemo(() => {
        return (
            fullTeam.find(
                (m) => m?.email && String(m.email).trim().toLowerCase() === email
            ) || null
        );
    }, [fullTeam, email]);

    const displayName =
        String(myProfile?.name || '').trim() ||
        (email ? email.split('@')[0] : '') ||
        'Coach';

    const canAccessPepulse = useMemo(() => {
        if (!email) return false;
        if (isMalusExemptEmail(email)) return true; // Christian Superadmin
        if (isSupportMember(myProfile)) return false;
        if (isPepulseEligibleMember(myProfile)) return true;
        // Hardcoded admins ohne Team-Eintrag
        if (email === 'coaching@preisexpert.info') return true;
        return false;
    }, [email, myProfile]);

    const pepulseTeam = useMemo(() => {
        return fullTeam.filter((m) => isPepulseEligibleMember(m) && m?.name);
    }, [fullTeam]);

    const [pendingType, setPendingType] = useState<SocialMediaLogType | null>(null);
    const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
    const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
    const [flashType, setFlashType] = useState<SocialMediaLogType | null>(null);
    const [progressScope, setProgressScope] = useState<ProgressScope>('month');
    const [showConfetti, setShowConfetti] = useState(false);
    const [vacationDraft, setVacationDraft] = useState(0);
    const [vacationSaving, setVacationSaving] = useState(false);
    const [scoringOpen, setScoringOpen] = useState(false);
    const confettiFiredRef = useRef(false);

    const showToast = useCallback((msg: string, type: 'success' | 'error' = 'success') => {
        setToast({ msg, type });
        window.setTimeout(() => setToast(null), 2500);
    }, []);

    useEffect(() => {
        if (!uid || !canAccessPepulse) return;
        subscribeMonthlyLogs(uid, month);
        subscribeMonthAllLogs(month);
        void fetchMonthMeta(month);
        return () => {
            unsubscribeMonthlyLogs();
            unsubscribeMonthAllLogs();
        };
    }, [
        uid,
        canAccessPepulse,
        month,
        subscribeMonthlyLogs,
        subscribeMonthAllLogs,
        unsubscribeMonthlyLogs,
        unsubscribeMonthAllLogs,
        fetchMonthMeta,
    ]);

    /** /pepulse: Favicon/Manifest + Canonical/OG + Härteprüfung gegen gecachte P-One-Nav */
    useEffect(() => {
        const path = String(window.location.pathname || '')
            .toLowerCase()
            .replace(/\/+$/, '') || '/';
        const isPepulseStandalone = path === '/pepulse';
        const isPepulseRoute = isPepulseStandalone || path.endsWith('/pepulse');
        if (!isPepulseRoute) return;

        const PEPULSE_CANONICAL = 'https://pe-one.friseur-coaching.de/pepulse';
        const DEFAULT_FAVICON = '/favicon-32.png?v=4';
        const DEFAULT_APPLE = '/logo192.png?v=4';
        const DEFAULT_MANIFEST = '/manifest.json';
        const DEFAULT_TITLE = document.title;

        const ensureLink = (rel: string, type?: string): HTMLLinkElement => {
            let el = document.head.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
            if (!el) {
                el = document.createElement('link');
                el.rel = rel;
                if (type) el.type = type;
                document.head.appendChild(el);
            }
            return el;
        };

        const ensureMeta = (attr: 'name' | 'property', key: string): HTMLMetaElement => {
            let el = document.head.querySelector(
                `meta[${attr}="${key}"]`
            ) as HTMLMetaElement | null;
            if (!el) {
                el = document.createElement('meta');
                el.setAttribute(attr, key);
                document.head.appendChild(el);
            }
            return el;
        };

        const iconLink = ensureLink('icon');
        const appleLink = ensureLink('apple-touch-icon');
        const manifestLink = ensureLink('manifest');
        const canonicalLink = ensureLink('canonical');
        const ogUrl = ensureMeta('property', 'og:url');
        const appleTitle = ensureMeta('name', 'apple-mobile-web-app-title');
        const appleCapable = ensureMeta('name', 'apple-mobile-web-app-capable');

        const prevIcon = iconLink.getAttribute('href') || DEFAULT_FAVICON;
        const prevApple = appleLink.getAttribute('href') || DEFAULT_APPLE;
        const prevManifest = manifestLink.getAttribute('href') || DEFAULT_MANIFEST;
        const prevCanonical = canonicalLink.getAttribute('href');
        const prevOg = ogUrl.getAttribute('content');
        const prevAppleTitle = appleTitle.getAttribute('content');
        const prevAppleCapable = appleCapable.getAttribute('content');

        iconLink.setAttribute('href', PE_PULSE_ICON_SRC);
        iconLink.setAttribute('type', 'image/png');
        iconLink.setAttribute('sizes', '512x512');
        appleLink.setAttribute('href', PE_PULSE_ICON_SRC);
        appleLink.setAttribute('sizes', '512x512');
        manifestLink.setAttribute('href', PE_PULSE_MANIFEST_HREF);
        canonicalLink.setAttribute('href', PEPULSE_CANONICAL);
        ogUrl.setAttribute('content', PEPULSE_CANONICAL);
        appleTitle.setAttribute('content', 'PEPulse');
        appleCapable.setAttribute('content', 'yes');
        document.title = 'PEPulse';

        /** Standalone: Portal-Chrome (CRM/Admin) aus gecachten Shells ausblenden */
        const hiddenNodes: HTMLElement[] = [];
        if (isPepulseStandalone) {
            document.body.classList.add('pepulse-pwa-isolated');
            const maybeHide = (el: Element | null) => {
                if (!(el instanceof HTMLElement)) return;
                if (el.dataset.pepulseIsolationHidden === '1') return;
                el.dataset.pepulseIsolationHidden = '1';
                el.style.setProperty('display', 'none', 'important');
                hiddenNodes.push(el);
            };
            document.querySelectorAll('aside').forEach((el) => maybeHide(el));
            document.querySelectorAll('header').forEach((el) => {
                const t = String(el.textContent || '');
                if (
                    t.includes('Coach Portal') ||
                    t.includes('ADMIN-PANEL') ||
                    t.includes('Mandanten-Akte') ||
                    t.includes('Finanz-Tresor') ||
                    t.includes('Rechnungs-Center')
                ) {
                    maybeHide(el);
                }
            });
            document.querySelectorAll('button, a').forEach((el) => {
                const t = String(el.textContent || '').trim();
                if (
                    t === 'Mandanten-Akte' ||
                    t === 'Finanz-Tresor' ||
                    t === 'Rechnungs-Center' ||
                    t.includes('ADMIN-PANEL')
                ) {
                    maybeHide(el);
                }
            });
        }

        return () => {
            iconLink.setAttribute('href', prevIcon);
            appleLink.setAttribute('href', prevApple);
            manifestLink.setAttribute('href', prevManifest);
            document.title = DEFAULT_TITLE;
            if (prevCanonical) canonicalLink.setAttribute('href', prevCanonical);
            else canonicalLink.remove();
            if (prevOg) ogUrl.setAttribute('content', prevOg);
            else ogUrl.remove();
            if (prevAppleTitle) appleTitle.setAttribute('content', prevAppleTitle);
            else appleTitle.remove();
            if (prevAppleCapable) appleCapable.setAttribute('content', prevAppleCapable);
            else appleCapable.remove();
            document.body.classList.remove('pepulse-pwa-isolated');
            hiddenNodes.forEach((el) => {
                el.style.removeProperty('display');
                delete el.dataset.pepulseIsolationHidden;
            });
        };
    }, []);

    const vacationDays = useMemo(() => {
        const meta = monthMetaByMonth?.[month]?.[uid];
        if (meta && meta.month && meta.month !== month) return 0;
        return clampVacationDays(meta?.vacationDays);
    }, [monthMetaByMonth, uid, month]);

    useEffect(() => {
        setVacationDraft(vacationDays);
    }, [vacationDays]);

    const todayIso = todayIsoLocal();

    const todayLogs = useMemo(() => {
        return logs
            .filter((l) => l.userId === uid && l.date === todayIso)
            .slice()
            .sort((a, b) => {
                const ta = formatLogTime(a.createdAt);
                const tb = formatLogTime(b.createdAt);
                if (ta && tb) return tb.localeCompare(ta);
                return String(b.id).localeCompare(String(a.id));
            });
    }, [logs, uid, todayIso]);

    const todayLogsByType = useMemo(() => {
        const groups: Record<SocialMediaLogType, SocialMediaLog[]> = {
            story: [],
            post: [],
            reel: [],
        };
        for (const log of todayLogs) {
            if (groups[log.type]) groups[log.type].push(log);
        }
        return groups;
    }, [todayLogs]);

    const todayStoryCount = useMemo(() => {
        let n = 0;
        for (const log of todayLogs) {
            if (log.type === 'story') n += log.count || 1;
        }
        return n;
    }, [todayLogs]);

    const todayPoints = useMemo(() => {
        let n = 0;
        for (const log of todayLogs) n += Number(log.points) || 0;
        return n;
    }, [todayLogs]);

    const weekCounts = useMemo(() => {
        const { startIso, endIso } = getIsoWeekRange(new Date());
        let posts = 0;
        let reels = 0;
        let stories = 0;
        let points = 0;
        for (const log of logs) {
            if (log.userId !== uid) continue;
            if (log.date < startIso || log.date > endIso) continue;
            if (log.type === 'post') posts += log.count || 1;
            if (log.type === 'reel') reels += log.count || 1;
            if (log.type === 'story') stories += log.count || 1;
            points += Number(log.points) || 0;
        }
        return { posts, reels, stories, points };
    }, [logs, uid]);

    const monthStats = useMemo(() => {
        const monthLogs = logs.filter((l) => l.userId === uid && l.month === month);
        return computeMonthlyStats(monthLogs, {
            userId: uid,
            month,
            vacationDays,
            email,
            isExemptFromMalus: isExempt,
        });
    }, [logs, uid, month, vacationDays, email, isExempt]);

    const cockpit = useMemo(() => {
        if (progressScope === 'day') {
            return {
                points: todayPoints,
                target: DAY_TARGET_POINTS,
                label: 'Tag',
                sub: `${todayStoryCount} / ${STORY_PER_DAY} Stories`,
                showMalus: false,
                malusRate: 0,
                percentLabel: Math.round((todayPoints / DAY_TARGET_POINTS) * 100),
            };
        }
        if (progressScope === 'week') {
            return {
                points: weekCounts.points,
                target: WEEK_TARGET_POINTS,
                label: 'Woche',
                sub: `${weekCounts.stories} / ${STORIES_PER_WEEK} S · ${weekCounts.posts} / ${POSTS_PER_WEEK} P · ${weekCounts.reels} / ${REELS_PER_WEEK} R`,
                showMalus: false,
                malusRate: 0,
                percentLabel: Math.round((weekCounts.points / WEEK_TARGET_POINTS) * 100),
            };
        }
        const vacHint =
            monthStats.vacationDays > 0 ? ` · ${monthStats.vacationDays} Urlaub` : '';
        return {
            points: monthStats.totalPoints,
            target: monthStats.targetPoints || SOCIAL_MEDIA_MONTHLY_TARGET_POINTS,
            label: 'Monat',
            sub: `${Math.round(monthStats.percent)}% Ziel · ${monthStats.malusRate}% Abzug${vacHint}`,
            showMalus: true,
            malusRate: monthStats.malusRate,
            percentLabel: Math.round(monthStats.percent),
        };
    }, [progressScope, todayPoints, todayStoryCount, weekCounts, monthStats]);

    const progressPct =
        cockpit.target > 0 ? Math.min(100, (cockpit.points / cockpit.target) * 100) : 0;

    /** Header-Ampel strikt nach progressScope (Tag / Woche / Monat-Pacing) */
    const headerAmpel = useMemo(() => {
        if (progressScope === 'day') {
            const color = scopePacingAmpel(todayPoints, DAY_TARGET_POINTS);
            return {
                color,
                title: `Pacing-Ampel Tag: ${color} (${formatPoints(todayPoints)} / ${formatPoints(DAY_TARGET_POINTS)} Pkt Tages-Soll)`,
                label: `Pacing-Ampel Tag ${color}`,
            };
        }
        if (progressScope === 'week') {
            const color = scopePacingAmpel(weekCounts.points, WEEK_TARGET_POINTS);
            return {
                color,
                title: `Pacing-Ampel Woche: ${color} (${formatPoints(weekCounts.points)} / ${formatPoints(WEEK_TARGET_POINTS)} Pkt Wochen-Soll)`,
                label: `Pacing-Ampel Woche ${color}`,
            };
        }
        const pacingSoll = calendarPacingSoll(
            monthStats.targetPoints || SOCIAL_MEDIA_MONTHLY_TARGET_POINTS,
            month
        );
        const color = scopePacingAmpel(monthStats.totalPoints, pacingSoll);
        return {
            color,
            title: `Pacing-Ampel Monat: ${color} (${formatPoints(monthStats.totalPoints)} / ${formatPoints(pacingSoll)} Pkt Soll-bis-heute; Monatsziel ${formatPoints(monthStats.targetPoints)} Pkt)`,
            label: `Pacing-Ampel Monat ${color}`,
        };
    }, [progressScope, todayPoints, weekCounts.points, monthStats, month]);

    const weekGoalMet = weekCounts.points >= WEEK_TARGET_POINTS;
    const monthGoalMet = monthStats.totalPoints >= monthStats.targetPoints;
    const cockpitGlow = weekGoalMet || monthGoalMet;

    useEffect(() => {
        if (!monthGoalMet || confettiFiredRef.current) return;
        confettiFiredRef.current = true;
        setShowConfetti(true);
        const t = window.setTimeout(() => setShowConfetti(false), 2200);
        return () => window.clearTimeout(t);
    }, [monthGoalMet]);

    const leaderboard = useMemo(() => {
        const monthLogs = monthAllLogs.filter((l) => l.month === month);

        const byEmail = new Map<string, number>();
        const byUid = new Map<string, number>();
        for (const log of monthLogs) {
            const pts = Number(log.points) || 0;
            if (log.userEmail) {
                byEmail.set(log.userEmail, (byEmail.get(log.userEmail) || 0) + pts);
            }
            if (log.userId) {
                byUid.set(log.userId, (byUid.get(log.userId) || 0) + pts);
            }
        }

        const rows = pepulseTeam.map((member, idx) => {
            const memberEmail = String(member.email || '').trim().toLowerCase();
            let points = memberEmail ? byEmail.get(memberEmail) || 0 : 0;
            if (points === 0 && memberEmail === email && uid) {
                points = byUid.get(uid) || 0;
            }
            return {
                id: String(member.id || memberEmail || idx),
                name: String(member.name || 'Coach').trim(),
                email: memberEmail,
                points,
                isMe: !!memberEmail && memberEmail === email,
                dot: RANK_DOTS[idx % RANK_DOTS.length],
            };
        });

        rows.sort((a, b) => b.points - a.points || a.name.localeCompare(b.name, 'de'));
        return rows;
    }, [monthAllLogs, pepulseTeam, email, uid, month]);

    const handleAdd = async (type: SocialMediaLogType) => {
        if (!uid || pendingType || !canAccessPepulse) return;
        setPendingType(type);
        try {
            const result = await addLog(uid, type, 1, undefined, {
                isExemptFromMalus: isExempt,
                userEmail: email || undefined,
                displayName: displayName || undefined,
            });
            if (result) {
                setFlashType(type);
                window.setTimeout(() => setFlashType(null), 400);
                try {
                    navigator.vibrate?.(30);
                } catch {
                    /* ignore */
                }
                showToast(`${TYPE_META[type].short} +${TYPE_META[type].points} Pkt`, 'success');
            } else {
                showToast(useSocialMediaStore.getState().error || 'Speichern fehlgeschlagen.', 'error');
            }
        } finally {
            setPendingType(null);
        }
    };

    const handleDelete = async (log: SocialMediaLog) => {
        if (pendingDeleteId) return;
        setPendingDeleteId(log.id);
        try {
            const ok = await deleteLog(log.id);
            if (ok) showToast('Eintrag gelöscht.', 'success');
            else showToast(useSocialMediaStore.getState().error || 'Löschen fehlgeschlagen.', 'error');
        } finally {
            setPendingDeleteId(null);
        }
    };

    const handleVacationSave = async () => {
        if (!uid || vacationSaving) return;
        const days = clampVacationDays(vacationDraft);
        setVacationSaving(true);
        try {
            const ok = await setVacationDays(uid, month, days, { userEmail: email || undefined });
            if (ok) {
                setVacationDraft(days);
                showToast(`Urlaub: ${days} Tage — Sollziel wird neu berechnet`, 'success');
            } else {
                showToast(
                    useSocialMediaStore.getState().error || 'Urlaub speichern fehlgeschlagen.',
                    'error'
                );
            }
        } finally {
            setVacationSaving(false);
        }
    };

    const bumpVacation = (delta: number) => {
        setVacationDraft((prev) => clampVacationDays(prev + delta));
    };

    if (!uid) {
        return (
            <div className="rounded-2xl border border-slate-800 bg-[#15171e] p-6 text-lg text-slate-400">
                PEPulse: bitte einloggen.
            </div>
        );
    }

    if (!canAccessPepulse) {
        return (
            <div className="rounded-2xl border border-slate-800 bg-[#15171e] p-6 text-lg text-slate-400">
                PEPulse ist nur für Coach und Admin verfügbar.
            </div>
        );
    }

    return (
        <section className="rounded-xl border border-slate-800 bg-[#15171e] shadow-xl overflow-hidden">
            <div className="px-4 pt-4 pb-3 space-y-3">
                <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                        <h1 className="flex items-center min-w-0 m-0">
                            <img
                                src={PE_PULSE_LOGO_SRC}
                                alt="PEPulse Logo"
                                className="h-24 w-auto object-contain"
                            />
                        </h1>
                        <span
                            className="w-2.5 h-2.5 rounded-full shrink-0"
                            style={{ backgroundColor: PACING_AMPEL_COLOR[headerAmpel.color] }}
                            title={headerAmpel.title}
                            aria-label={headerAmpel.label}
                        />
                    </div>
                    <div className="inline-flex rounded-lg border border-slate-700 bg-[#0c0d11] p-0.5">
                        {(
                            [
                                { id: 'day', label: 'Tag' },
                                { id: 'week', label: 'Woche' },
                                { id: 'month', label: 'Monat' },
                            ] as { id: ProgressScope; label: string }[]
                        ).map((opt) => (
                            <button
                                key={opt.id}
                                type="button"
                                onClick={() => setProgressScope(opt.id)}
                                className={`px-2.5 py-1 rounded-md text-sm font-bold transition-colors ${
                                    progressScope === opt.id
                                        ? 'bg-[#E6C299]/20 text-[#E6C299]'
                                        : 'text-slate-500 hover:text-slate-300'
                                }`}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                </div>

                {progressScope === 'month' && monthStats.malusRate > 0 && !isExempt ? (
                    <div className="rounded-lg border border-red-500/50 bg-red-950/40 px-3 py-2 text-sm font-bold text-red-300">
                        Punktabzug aktiv: −{monthStats.malusRate}% bei {Math.round(monthStats.percent)}%
                        Monatsziel (Ampel zeigt Pacing bis heute, nicht den Abrechnungs-Malus)
                    </div>
                ) : null}

                {/* Progress-Cockpit */}
                <div
                    className={`relative rounded-xl border bg-gradient-to-b from-[#1a1814] to-[#0c0d11] px-4 py-3 transition-shadow duration-500 ${
                        cockpitGlow
                            ? 'border-[#E6C299] shadow-[0_0_28px_rgba(230,194,153,0.45)]'
                            : 'border-[#E6C299]/25 shadow-[inset_0_1px_0_rgba(230,194,153,0.12)]'
                    }`}
                >
                    <ConfettiBurst active={showConfetti} />
                    <div className="mb-2 flex items-baseline justify-between gap-3 flex-wrap relative z-10">
                        <div className="text-3xl font-black text-white tracking-tight tabular-nums leading-none">
                            {formatPoints(cockpit.points)}
                            <span className="text-lg font-bold text-slate-500">
                                {' '}
                                / {formatPoints(cockpit.target)} Pkt
                            </span>
                        </div>
                        <div className="text-base font-bold text-[#E6C299]">{cockpit.sub}</div>
                    </div>

                    <div className="relative h-3 w-full rounded-full bg-slate-900/80 border border-slate-700/80 overflow-hidden z-10">
                        <div
                            className="absolute inset-y-0 left-0 rounded-full transition-all duration-500 ease-out"
                            style={{
                                width: `${progressPct}%`,
                                background:
                                    'linear-gradient(90deg, #c4a06e 0%, #E6C299 45%, #f0d4b0 100%)',
                                boxShadow: cockpitGlow
                                    ? '0 0 20px rgba(230,194,153,0.85), 0 0 4px rgba(230,194,153,1)'
                                    : '0 0 14px rgba(230,194,153,0.6), 0 0 3px rgba(230,194,153,0.85)',
                            }}
                        />
                        <div
                            className="pointer-events-none absolute inset-y-0 left-0 rounded-full opacity-40"
                            style={{
                                width: `${progressPct}%`,
                                background:
                                    'linear-gradient(180deg, rgba(255,255,255,0.35) 0%, transparent 55%)',
                            }}
                        />
                    </div>
                </div>

                {/* Transparenz: Wie wird gerechnet? */}
                <div className="rounded-lg border border-slate-700 bg-[#0c0d11] overflow-hidden">
                    <button
                        type="button"
                        onClick={() => setScoringOpen((v) => !v)}
                        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left hover:bg-slate-900/40"
                        aria-expanded={scoringOpen}
                    >
                        <span className="text-sm font-black uppercase tracking-wider text-[#E6C299]">
                            Wie wird gerechnet?
                        </span>
                        <ChevronDown
                            size={18}
                            className={`text-slate-400 shrink-0 transition-transform ${
                                scoringOpen ? 'rotate-180' : ''
                            }`}
                        />
                    </button>
                    {scoringOpen ? (
                        <div className="px-3 pb-3 space-y-2.5 border-t border-slate-800 text-sm text-slate-300">
                            <div>
                                <div className="text-xs font-black uppercase tracking-wider text-slate-500 mb-1">
                                    Punkte-Basis
                                </div>
                                <ul className="space-y-0.5 font-bold">
                                    <li>+{SOCIAL_MEDIA_POINTS.story} Punkte pro Story</li>
                                    <li>+{SOCIAL_MEDIA_POINTS.post} Punkte pro Beitrag</li>
                                    <li>+{SOCIAL_MEDIA_POINTS.reel} Punkte pro Reel</li>
                                </ul>
                            </div>
                            <div>
                                <div className="text-xs font-black uppercase tracking-wider text-slate-500 mb-1">
                                    Urlaubstage-Effekt
                                </div>
                                <p className="leading-snug">
                                    Jeder eingetragene Urlaubstag reduziert automatisch das Monats-Sollziel
                                    (Basis {SOCIAL_MEDIA_MONTHLY_TARGET_POINTS} Pkt bei 0 Urlaubstagen; max. 5
                                    Urlaubstage pro Woche / {SOCIAL_MEDIA_VACATION_BASE_DAYS} Tage pro Monat).
                                    Die Erfüllung % und der Abzug werden danach neu berechnet.
                                </p>
                            </div>
                            <div>
                                <div className="text-xs font-black uppercase tracking-wider text-slate-500 mb-1">
                                    Abzugs-Staffelung (nach Zielerfüllung)
                                </div>
                                <ul className="space-y-1 leading-snug">
                                    <li>
                                        <span className="text-emerald-400 font-bold">100 %+:</span> kein
                                        Abzug
                                    </li>
                                    <li>
                                        <span className="text-yellow-400 font-bold">Ab 80 %:</span> 3 %
                                        Abzug (gilt für 80–99 %)
                                    </li>
                                    <li>
                                        <span className="text-orange-400 font-bold">Ab 60 %:</span> 6 %
                                        Abzug (gilt für 60–79 %)
                                    </li>
                                    <li>
                                        <span className="text-red-400 font-bold">Unter 60 %:</span> 10 %
                                        Höchststrafe
                                    </li>
                                </ul>
                                <p className="mt-1.5 text-xs text-slate-500 leading-snug">
                                    Es greift immer nur die eine Stufe, die zu deiner aktuellen % passt —
                                    nicht addiert. Bei wirksamem Abzug oder 0 % Erfüllung ist die Ampel
                                    nie grün.
                                </p>
                            </div>
                        </div>
                    ) : null}
                </div>

                {/* Urlaubstage (Lucy Pro-Rata) */}
                <div className="rounded-lg border border-slate-800 bg-[#0c0d11] px-3 py-2.5 space-y-2">
                    <div className="flex flex-wrap items-center gap-3">
                        <span className="text-sm font-bold text-slate-400">
                            Urlaubstage ({month})
                        </span>
                        <div className="inline-flex items-center gap-2">
                            <button
                                type="button"
                                disabled={vacationSaving || vacationDraft <= 0}
                                onClick={() => bumpVacation(-1)}
                                className="touch-manipulation w-11 h-11 rounded-lg border border-slate-600 bg-[#15171e] text-white flex items-center justify-center hover:border-[#E6C299] disabled:opacity-40"
                                aria-label="Urlaubstag weniger"
                            >
                                <Minus size={20} />
                            </button>
                            <span
                                className="min-w-[2.5rem] text-center text-xl font-black tabular-nums text-white"
                                aria-live="polite"
                            >
                                {vacationDraft}
                            </span>
                            <button
                                type="button"
                                disabled={
                                    vacationSaving || vacationDraft >= SOCIAL_MEDIA_VACATION_BASE_DAYS
                                }
                                onClick={() => bumpVacation(1)}
                                className="touch-manipulation w-11 h-11 rounded-lg border border-slate-600 bg-[#15171e] text-white flex items-center justify-center hover:border-[#E6C299] disabled:opacity-40"
                                aria-label="Urlaubstag mehr"
                            >
                                <Plus size={20} />
                            </button>
                        </div>
                        <button
                            type="button"
                            disabled={vacationSaving || vacationDraft === vacationDays}
                            onClick={() => void handleVacationSave()}
                            className="ml-auto text-xs font-black uppercase tracking-wider px-3 py-2 rounded border border-[#E6C299]/40 text-[#E6C299] hover:bg-[#E6C299]/15 disabled:opacity-40"
                        >
                            {vacationSaving ? '…' : 'Speichern'}
                        </button>
                    </div>
                    <p className="text-xs text-slate-500 leading-snug">
                        Pro Woche können maximal 5 Tage Urlaub eingetragen werden (max.{' '}
                        {SOCIAL_MEDIA_VACATION_BASE_DAYS} Tage pro Monat). Plus/Minus einstellen, dann
                        Speichern.
                    </p>
                </div>
            </div>

            {/* Quick-Buttons */}
            <div className="px-4 pb-3 space-y-2">
                <div className="grid grid-cols-3 gap-2">
                    {TYPE_ORDER.map((type) => {
                        const meta = TYPE_META[type];
                        const Icon = meta.Icon;
                        const busy = pendingType === type;
                        const disabled = !!pendingType;
                        const flashed = flashType === type;
                        const ownContentHint = `${meta.label}: nur selbst erstellter Content — keine Reposts, geteilten Stories/Beiträge oder Co-Autoren- und Coop-Partner-Markierungen.`;
                        return (
                            <button
                                key={type}
                                type="button"
                                disabled={disabled}
                                onClick={() => void handleAdd(type)}
                                title={ownContentHint}
                                aria-label={ownContentHint}
                                className={[
                                    'touch-manipulation min-h-[68px] rounded-xl border px-2 py-2.5',
                                    'flex flex-col items-center justify-center gap-1',
                                    'font-bold uppercase tracking-wide text-xs sm:text-sm leading-tight transition-all active:scale-[0.96]',
                                    flashed
                                        ? 'bg-[#E6C299]/30 border-[#E6C299] text-[#E6C299] shadow-[0_0_20px_rgba(230,194,153,0.3)]'
                                        : 'bg-[#0c0d11] border-slate-700 text-slate-100 hover:border-[#E6C299]/70 hover:text-[#E6C299]',
                                    disabled && !busy ? 'opacity-50 cursor-not-allowed' : '',
                                    busy ? 'opacity-80' : '',
                                ].join(' ')}
                            >
                                {busy ? (
                                    <Loader2 size={18} className="animate-spin text-[#E6C299]" />
                                ) : (
                                    <Icon size={18} className="text-[#E6C299]" />
                                )}
                                <span className="text-center">{meta.label}</span>
                                <span className="text-[11px] font-bold text-slate-500 normal-case tracking-normal">
                                    +{meta.points}
                                </span>
                            </button>
                        );
                    })}
                </div>
                <div className="rounded-lg border border-[#E6C299]/35 bg-[#0c0d11] px-3 py-2">
                    <p className="text-sm font-bold text-[#E6C299] leading-snug">
                        📌 WICHTIG: Nur EIGENER Content zählt! Reposts, geteilte Stories/Beiträge sowie
                        Co-Autoren- und Coop-Partner-Markierungen sind nicht anrechenbar.
                    </p>
                </div>
            </div>

            {error && (
                <div className="px-4 pb-2 text-sm text-red-400 font-bold">{error}</div>
            )}

            <div className="px-4 pb-4 space-y-3 border-t border-slate-800/80 pt-3">
                {/* Ziele + Ist */}
                <div className="rounded-xl border border-slate-800 bg-[#0c0d11] px-3.5 py-2.5">
                    <p className="text-base font-bold text-white leading-snug">
                        Tages-Ziel: {STORY_PER_DAY} Stories{' '}
                        <span className="text-sm font-bold text-slate-500">(bei 5-Tage-Woche)</span>
                    </p>
                    <p className="text-sm font-bold text-slate-500 mt-0.5">
                        Wochen-Ziel: {STORIES_PER_WEEK} Stories • {POSTS_PER_WEEK} Posts •{' '}
                        {REELS_PER_WEEK} Reel
                    </p>
                    <p className="text-base font-bold text-white mt-2">
                        Heute:{' '}
                        <span className="text-[#E6C299]">
                            {todayStoryCount} / {STORY_PER_DAY} Stories
                        </span>
                        <span className="text-slate-600"> · </span>
                        Woche:{' '}
                        <span className="text-[#E6C299]">
                            {weekCounts.stories} / {STORIES_PER_WEEK} S
                        </span>
                        <span className="text-slate-600"> · </span>
                        <span className="text-[#E6C299]">
                            {weekCounts.posts} / {POSTS_PER_WEEK} P
                        </span>
                        <span className="text-slate-600"> · </span>
                        <span className="text-[#E6C299]">
                            {weekCounts.reels} / {REELS_PER_WEEK} R
                        </span>
                    </p>
                </div>

                <div>
                    <div className="text-sm font-bold text-slate-500 mb-1">Heute erfasst</div>
                    {todayLogs.length === 0 ? (
                        <div className="rounded-lg border border-dashed border-slate-800 px-3 py-2 text-center text-sm text-slate-600">
                            Noch nichts erfasst.
                        </div>
                    ) : (
                        <div className="space-y-2 max-h-56 overflow-y-auto">
                            {TYPE_ORDER.map((type) => {
                                const group = todayLogsByType[type];
                                if (!group.length) return null;
                                const meta = TYPE_META[type];
                                return (
                                    <div
                                        key={type}
                                        className="rounded-lg border border-slate-800 overflow-hidden"
                                    >
                                        <div className="px-2.5 py-1 bg-slate-900/60 text-xs font-black uppercase tracking-wider text-slate-400">
                                            {meta.short === 'Story'
                                                ? 'Stories'
                                                : meta.short === 'Beitrag'
                                                  ? 'Beiträge'
                                                  : 'Reels'}{' '}
                                            ({group.reduce((n, l) => n + (l.count || 1), 0)})
                                        </div>
                                        <ul className="divide-y divide-slate-800/80">
                                            {group.map((log) => {
                                                const deleting = pendingDeleteId === log.id;
                                                const time = formatLogTime(log.createdAt);
                                                return (
                                                    <li
                                                        key={log.id}
                                                        className="flex items-center gap-2 px-2.5 py-1 bg-[#0c0d11]/50"
                                                    >
                                                        <span className="text-sm font-bold text-white truncate min-w-0">
                                                            {meta.short}
                                                            <span className="text-[#E6C299]">
                                                                {' '}
                                                                +{log.points}
                                                            </span>
                                                            {time ? (
                                                                <span className="text-slate-600 font-medium">
                                                                    {' '}
                                                                    · {time}
                                                                </span>
                                                            ) : null}
                                                        </span>
                                                        <button
                                                            type="button"
                                                            disabled={!!pendingDeleteId}
                                                            onClick={() => void handleDelete(log)}
                                                            className="touch-manipulation shrink-0 ml-auto p-1.5 rounded-md text-slate-600 hover:text-red-400 disabled:opacity-40"
                                                            title="Löschen"
                                                            aria-label="Löschen"
                                                        >
                                                            {deleting ? (
                                                                <Loader2
                                                                    size={14}
                                                                    className="animate-spin"
                                                                />
                                                            ) : (
                                                                <Trash2 size={14} />
                                                            )}
                                                        </button>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Rangliste */}
                <div className="rounded-xl border border-slate-800/80 bg-[#0c0d11]/50 px-3 py-2">
                    <div className="text-sm font-bold text-slate-500 mb-1">Rangliste</div>
                    {leaderboard.length === 0 ? (
                        <p className="text-sm text-slate-600">Keine Coaches im Team-Register.</p>
                    ) : (
                        <ul className="space-y-0.5">
                            {leaderboard.map((row) => (
                                <li
                                    key={row.id}
                                    className={`flex items-center justify-between gap-2 rounded-md px-2 py-0.5 ${
                                        row.isMe ? 'bg-[#E6C299]/10' : ''
                                    }`}
                                >
                                    <div className="flex items-center gap-2 min-w-0">
                                        <span
                                            className="w-2 h-2 rounded-full shrink-0"
                                            style={{ backgroundColor: row.dot }}
                                        />
                                        <span
                                            className={`text-sm font-medium truncate ${
                                                row.isMe ? 'text-[#E6C299]' : 'text-slate-400'
                                            }`}
                                        >
                                            {row.name}
                                        </span>
                                    </div>
                                    <span className="text-sm font-medium text-slate-500 tabular-nums shrink-0">
                                        {formatPoints(row.points)}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {isAdmin && (
                    <PepulseAdminCockpit
                        pepulseTeam={pepulseTeam}
                        payoutBaseByEmail={payoutBaseByEmail}
                    />
                )}
            </div>

            {toast && (
                <div
                    className={`fixed bottom-24 left-1/2 -translate-x-1/2 z-[100000] px-5 py-2.5 rounded-full shadow-2xl text-base font-bold text-white pointer-events-none ${
                        toast.type === 'success'
                            ? 'bg-emerald-600/90 border border-emerald-400'
                            : 'bg-red-600/90 border border-red-400'
                    }`}
                >
                    {toast.msg}
                </div>
            )}
        </section>
    );
};

export default MobileTrackerView;
